import { createContext, useContext, useMemo, useState } from 'react';
import type { RunStatus, RunSummary } from '@shared/types/factory';
import { useLoader } from '@/hooks/use-loader';
import { listAllRuns, listRuns } from '@/lib/runs-api';

/** The Board's live runs: each card with one shows how far it's got (TaskCardView's stripe). */

const ACTIVE: readonly RunStatus[] = ['queued', 'running', 'waiting'];
/** While a run is going, as often as the task's runs list. */
const BUSY_POLL_MS = 5000;
/** Otherwise just often enough to see a run that has started (assigning the AI builder starts one). */
const IDLE_POLL_MS = 20_000;

/** Task id → its newest run that hasn't finished. Empty outside a Board. */
export const TaskActiveRunsContext = createContext<ReadonlyMap<string, RunSummary>>(new Map());

/** The task's run that's going, if any, from the Board's TaskActiveRunsContext. */
export function useTaskActiveRun(taskId: string): RunSummary | undefined {
  return useContext(TaskActiveRunsContext).get(taskId);
}

/**
 * SF-T-278: the runs going on these sub-tasks of `parentId`, each once (a run building several of them shows
 * against the first), in the sub-tasks' order. A run of the parent itself is left out: it has its own stripe.
 */
export function useSubtaskActiveRuns(parentId: string, subtaskIds: readonly string[]): Array<{ taskId: string; run: RunSummary }> {
  const byTask = useContext(TaskActiveRunsContext);
  const seen = new Set<string>();
  const out: Array<{ taskId: string; run: RunSummary }> = [];
  for (const taskId of subtaskIds) {
    const run = byTask.get(taskId);
    if (!run || run.task?.id === parentId || seen.has(run.id)) continue;
    seen.add(run.id);
    out.push({ taskId, run });
  }
  return out;
}

/** The project's runs that haven't finished, by task, polled while the Board is open. */
export function useProjectActiveRuns(projectSlug: string): ReadonlyMap<string, RunSummary> {
  const [busy, setBusy] = useState(false);
  const loader = useLoader(
    (signal) => listRuns({ status: ACTIVE, limit: 100 }, { signal, project: projectSlug }),
    [projectSlug],
    { enabled: Boolean(projectSlug), pollMs: busy ? BUSY_POLL_MS : IDLE_POLL_MS }
  );
  return useRunsByTask(loader.data?.data?.runs, busy, setBusy);
}

/** Every visible project's runs that haven't finished, by task: the master board's (T-26). */
export function useAllActiveRuns(): ReadonlyMap<string, RunSummary> {
  const [busy, setBusy] = useState(false);
  const loader = useLoader((signal) => listAllRuns({ status: ACTIVE, limit: 100 }, { signal }), [], {
    pollMs: busy ? BUSY_POLL_MS : IDLE_POLL_MS,
  });
  return useRunsByTask(loader.data?.data?.runs, busy, setBusy);
}

function useRunsByTask(runs: readonly RunSummary[] | undefined, busy: boolean, setBusy: (busy: boolean) => void): ReadonlyMap<string, RunSummary> {
  const nowBusy = Boolean(runs?.length);
  if (nowBusy !== busy) setBusy(nowBusy);

  return useMemo(() => {
    const byTask = new Map<string, RunSummary>();
    // Newest first: the first run seen for a task is its latest.
    for (const run of runs ?? []) {
      if (run.task && !byTask.has(run.task.id)) byTask.set(run.task.id, run);
      // A run that builds sub-tasks (T-42) shows on each of them too.
      for (const sub of run.subtasks ?? []) if (!byTask.has(sub.id)) byTask.set(sub.id, run);
    }
    return byTask;
  }, [runs]);
}
